import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import {
  entrance,
  fadeIn,
  slideUp,
  staggerDelay,
  breathe,
  pulse,
  float,
  SPRINGS,
} from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * ObjectShowcase — 3D product card con perspectiva y etiquetas animadas.
 *
 * params: { object_name, labels: [{key, value}], accent_color }
 */
export default function ObjectShowcase({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps, durationInFrames } = useVideoConfig()
  const { object_name = 'Objeto', labels = [], accent_color = COLORS.accent.blue } = params

  // Entrance animations
  const cardEntrance = entrance(frame, fps, 0, SPRINGS.gentle)
  const titleOpacity = fadeIn(frame, 8, 20)
  const titleY = slideUp(frame, fps, 8)

  // 3D rotation — slow oscillation around Y axis
  const rotateY = interpolate(
    frame,
    [0, durationInFrames * 0.3, durationInFrames * 0.7, durationInFrames],
    [12, -4, 4, -8],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  )
  const rotateX = breathe(frame, 0.015, 2.5) - 1 // subtle X tilt

  // Hold-phase micro-animations
  const cardBreath = breathe(frame, 0.025, 0.008)
  const glowPulse = pulse(frame, 0.05, 0.5, 1.0)
  const ringRotation = frame * 0.2

  // Shine effect — traveling highlight across the card
  const shinePos = interpolate(frame, [20, 60], [-100, 200], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  return (
    <SceneWrapper accent={accent_color} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          perspective: 1200,
        }}
      >
        {/* 3D Card container */}
        <div
          style={{
            transform: `rotateY(${rotateY}deg) rotateX(${rotateX}deg) scale(${cardEntrance * cardBreath})`,
            transformStyle: 'preserve-3d',
            marginBottom: 40,
            position: 'relative',
          }}
        >
          {/* Outer orbit ring */}
          <div
            style={{
              position: 'absolute',
              width: 380,
              height: 380,
              borderRadius: '50%',
              border: `1.5px dashed ${accent_color}18`,
              top: -30,
              left: -30,
              transform: `rotate(${ringRotation}deg)`,
              opacity: fadeIn(frame, 10, 20) * 0.6,
            }}
          />
          {/* Inner orbit ring */}
          <div
            style={{
              position: 'absolute',
              width: 420,
              height: 420,
              borderRadius: '50%',
              border: `1px dashed ${accent_color}0D`,
              top: -50,
              left: -50,
              transform: `rotate(${-ringRotation * 0.5}deg)`,
              opacity: fadeIn(frame, 15, 20) * 0.4,
            }}
          />

          {/* Main product card */}
          <div
            style={{
              width: 320,
              height: 320,
              borderRadius: 32,
              ...GLASS.card,
              border: `1.5px solid ${accent_color}30`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: `${GLOW.lg(accent_color)}, 0 30px 60px rgba(0,0,0,0.5)`,
            }}
          >
            {/* Shine sweep */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: shinePos,
                width: 80,
                height: '100%',
                background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)`,
                transform: 'skewX(-15deg)',
                pointerEvents: 'none',
              }}
            />

            {/* Top accent line */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '15%',
                width: '70%',
                height: 2,
                background: `linear-gradient(90deg, transparent, ${accent_color}60, transparent)`,
                borderRadius: 1,
              }}
            />

            {/* Category label */}
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 14,
                color: accent_color,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                marginBottom: 12,
                textShadow: `0 0 8px ${accent_color}40`,
              }}
            >
              {object_name.split(' ')[0]}
            </div>

            {/* Product name */}
            <div
              style={{
                fontFamily: FONTS.heading,
                fontSize: object_name.length > 20 ? 28 : 36,
                fontWeight: 700,
                color: COLORS.text.primary,
                textAlign: 'center',
                lineHeight: 1.15,
                padding: '0 24px',
                maxWidth: 280,
              }}
            >
              {object_name.includes(' ') ? object_name.split(' ').slice(1).join(' ') : object_name}
            </div>

            {/* Accent dot */}
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: accent_color,
                marginTop: 16,
                boxShadow: GLOW.sm(accent_color),
                opacity: glowPulse,
              }}
            />
          </div>

          {/* Shadow plane for 3D depth */}
          <div
            style={{
              position: 'absolute',
              bottom: -20,
              left: '10%',
              width: '80%',
              height: 40,
              background: `radial-gradient(ellipse, ${accent_color}15 0%, transparent 70%)`,
              filter: 'blur(15px)',
              transform: 'rotateX(90deg)',
              transformOrigin: 'top center',
            }}
          />
        </div>

        {/* Object name subtitle */}
        <div
          style={{
            opacity: titleOpacity,
            transform: `translateY(${titleY}px)`,
            fontFamily: FONTS.heading,
            fontSize: 24,
            fontWeight: 600,
            color: COLORS.text.secondary,
            marginBottom: 36,
            letterSpacing: '0.04em',
          }}
        >
          {object_name}
        </div>

        {/* Spec labels */}
        {labels.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 14,
              justifyContent: 'center',
              maxWidth: 1400,
              padding: '0 80px',
            }}
          >
            {labels.slice(0, 5).map((label, i) => {
              const delay = staggerDelay(i, 5) + 18
              const itemOpacity = fadeIn(frame, delay, 15)
              const itemY = slideUp(frame, fps, delay, 24)
              const itemFloat = float(frame, 0.02 + i * 0.004, 2.5)
              return (
                <SpecLabel
                  key={i}
                  label={label}
                  accentColor={accent_color}
                  opacity={itemOpacity}
                  translateY={itemY + itemFloat}
                  index={i}
                />
              )
            })}
          </div>
        )}
      </div>
    </SceneWrapper>
  )
}

function SpecLabel({ label, accentColor, opacity, translateY, index }) {
  const palette = [
    COLORS.accent.blue,
    COLORS.accent.teal,
    COLORS.accent.purple,
    COLORS.accent.green,
    COLORS.accent.orange,
  ]
  const color = accentColor || palette[index % palette.length]

  return (
    <div
      style={{
        opacity,
        transform: `translateY(${translateY}px)`,
        ...GLASS.chip,
        border: `1px solid ${color}20`,
        borderRadius: 14,
        padding: '12px 22px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        minWidth: 130,
      }}
    >
      <span
        style={{
          fontFamily: FONTS.heading,
          fontSize: 26,
          fontWeight: 700,
          color: COLORS.text.primary,
        }}
      >
        {label.value}
      </span>
      <span
        style={{
          fontFamily: FONTS.body,
          fontSize: 12,
          color: COLORS.text.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
        }}
      >
        {label.key}
      </span>
    </div>
  )
}
